import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Typography, Box, Collapse, IconButton, styled, alpha, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Paper } from '@mui/material';
import { ExpandMore, ChevronRight, Add, Edit, Delete } from '@mui/icons-material';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client/react';
import { useParams } from 'react-router-dom';
import { PROJECT_TREE_QUERY, MOVE_ELEMENT, SAVE_PROJECT, GET_COMPANIES, DELETE_PROJECT, GET_ELEMENT } from '../../graphql/projects';
import ProjectModal from './ProjectModal';
import LoadingOverlay from '../common/LoadingOverlay';



const TreeItemContent = styled('div')(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  width: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  borderRadius: theme.spacing(0.5),
  marginBottom: 1,
  fontWeight: 500,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const TreeItemHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  height: 36,
}));

const TreeItemLabelText = styled(Typography)({
  color: 'inherit',
  fontWeight: 500,
});

const CustomTreeItem = ({ item, depth = 0, draggedItem, setDraggedItem, dropTarget, setDropTarget, onDrop, expandedItems, setExpandedItems, showDetails, handleAddElement, handleEditElement, handleDeleteElement, selectedUnit, onUnitSelect }) => {
  const itemRef = React.useRef(null);
  const expanded = expandedItems.has(item.id);
  const hasChildren = item.children && item.children.length > 0;
  const canDrop = true;
  const isDragging = draggedItem?.id === item.id;
  const isDropTarget = dropTarget === item.id;
  const isSelected = selectedUnit === item.label;

  React.useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isSelected]);

  const handleToggle = () => {
    if (hasChildren) {
      const newExpanded = new Set(expandedItems);
      if (expanded) {
        newExpanded.delete(item.id);
      } else {
        newExpanded.add(item.id);
      }
      setExpandedItems(newExpanded);
    }
  };

  const handleDragStart = (e) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    if (canDrop && draggedItem?.id !== item.id) {
      e.preventDefault();
      setDropTarget(item.id);
    }
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    onDrop(item.id);
  };

  return (
    <Box sx={{ ml: depth * 2 }} ref={itemRef}>
      <TreeItemContent
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          opacity: isDragging ? 0.5 : 1,
          backgroundColor: isDropTarget ? 'action.hover' : 'transparent',
          border: isDropTarget ? '2px dashed #1976d2' : 'none',
        }}
      >
        <TreeItemHeader>
          <Box 
            sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={handleToggle}
          >
            {hasChildren && (
              <IconButton 
                size="small" 
                sx={{ 
                  width: 20, 
                  height: 20, 
                  p: 0,
                  '& .MuiSvgIcon-root': { fontSize: '1rem' }
                }}
              >
                {expanded ? <ExpandMore /> : <ChevronRight />}
              </IconButton>
            )}
          </Box>
          <TreeItemLabelText 
            variant="subtitle1" 
            sx={{ 
              flexGrow: 1,
              backgroundColor: isSelected ? '#2196F3' : 'transparent',
              color: isSelected ? 'white' : 'inherit',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onUnitSelect?.(item.label, item.id);
            }}
          >
            {item.label}{item.number ? ` (${item.number})` : ''}
          </TreeItemLabelText>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton 
              size="small" 
              color="success"
              title="Add child"
              onClick={(e) => {
                e.stopPropagation();
                handleAddElement(item);
              }}
            >
              <Add fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              color="primary"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                handleEditElement(item);
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              color="error"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteElement(item);
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </TreeItemHeader>
        {showDetails && (
          <Box sx={{ ml: '28px', mb: 0.5 }}>
            {(item.planned_start_date || item.planned_end_date) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Planned dates: {item.planned_start_date} - {item.planned_end_date}
              </Typography>
            )}
            {item.companies && item.companies.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Companies: {item.companies.join(', ')}
              </Typography>
            )}
            {(item.customer || item.conditions_of_acceptance) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {[item.customer && `Customer: ${item.customer}`, item.conditions_of_acceptance && `Conditions: ${item.conditions_of_acceptance}`].filter(Boolean).join(', ')}
              </Typography>
            )}
          </Box>
        )}
      </TreeItemContent>
      {hasChildren && (
        <Collapse in={expanded}>
          <Box>
            {item.children.map(child => (
              <CustomTreeItem 
                key={child.id} 
                item={child} 
                depth={depth + 1}
                draggedItem={draggedItem}
                setDraggedItem={setDraggedItem}
                dropTarget={dropTarget}
                setDropTarget={setDropTarget}
                onDrop={onDrop}
                expandedItems={expandedItems}
                setExpandedItems={setExpandedItems}
                showDetails={showDetails}
                handleAddElement={handleAddElement}
                handleEditElement={handleEditElement}
                handleDeleteElement={handleDeleteElement}
                selectedUnit={selectedUnit}
                onUnitSelect={onUnitSelect}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const ProjectTree = forwardRef(({ parentId, selectedUnit, onUnitSelect, onTreeDataChange }, ref) => {
  const { id: projectId } = useParams();
  const [items, setItems] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showDetails, setShowDetails] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingElement, setEditingElement] = useState(null);
  const [parentElement, setParentElement] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [elementToDelete, setElementToDelete] = useState(null);
  
  const { data, loading, error, refetch } = useQuery(PROJECT_TREE_QUERY, {
    variables: { projectId },
    skip: !projectId
  });
  const { data: companiesData } = useQuery(GET_COMPANIES);
  const [getElement] = useLazyQuery(GET_ELEMENT);
  
  const [moveElement] = useMutation(MOVE_ELEMENT);
  const [saveProject] = useMutation(SAVE_PROJECT);
  const [deleteProject] = useMutation(DELETE_PROJECT);

  useImperativeHandle(ref, () => ({
    refetch
  }));

  useEffect(() => {
    if (data?.project_tree) {
      try {
        const treeData = JSON.parse(data.project_tree);
        const itemsArray = Array.isArray(treeData) ? treeData : [treeData];
        setItems(itemsArray);
        
        // Build label to ID and ID to label maps
        const buildMaps = (items, labelToId = {}, idToLabel = {}) => {
          items.forEach(item => {
            labelToId[item.label] = item.id;
            idToLabel[item.id] = item.label;
            if (item.children) {
              buildMaps(item.children, labelToId, idToLabel);
            }
          });
          return { labelToId, idToLabel };
        };
        
        const { labelToId, idToLabel } = buildMaps(itemsArray);
        
        // Only update if maps actually changed
        if (onTreeDataChange && Object.keys(labelToId).length > 0) {
          onTreeDataChange(labelToId, idToLabel);
        }
      } catch (parseError) {
        // Handle parse error silently
      }
    }
  }, [data]);

  const handleAddElement = (parent = null) => {
    setEditingElement(null);
    setParentElement(parent);
    setModalOpen(true);
  };

  const handleEditElement = async (element, parent = null) => {
    try {
      const { data: elementData } = await getElement({ 
        variables: { id: element.id } 
      });
      setEditingElement(elementData?.element);
      setParentElement(parent);
      setModalOpen(true);
    } catch (error) {
      // Handle error silently
    }
  };

  const handleSaveElement = async (elementData) => {
    try {
      const attributes = {
        ...elementData,
        parent_id: parentElement ? parseInt(parentElement.id) : parseInt(projectId)
      };
      if (editingElement) {
        attributes.id = parseInt(editingElement.id);
      }
      const result = await saveProject({ variables: { attributes } });
      
      // Zamknij modal najpierw
      setModalOpen(false);
      setEditingElement(null);
      setParentElement(null);
      
      // Potem odśwież dane
      await refetch();
      
      return result.data.createOrUpdateProjects.project;
    } catch (error) {
      throw error;
    }
  };

  const handleUploadComplete = async () => {
    // Compatibility function
  };

  const handleDeleteElement = (element) => {
    setElementToDelete(element);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    // Zamknij dialog najpierw
    setDeleteDialogOpen(false);
    const elementId = elementToDelete.id.toString();
    setElementToDelete(null);
    
    try {
      await deleteProject({ variables: { id: elementId } });
      await refetch();
    } catch (error) {
      // Handle error silently
    }
  };



  const handleDrop = async (targetId) => {
    if (!draggedItem || draggedItem.id === targetId) return;
    
    try {
      const variables = {
        element_id: draggedItem.id,
        target_id: targetId === 'root' ? projectId : targetId
      };
      
      const result = await moveElement({ variables });
      
      if (result.data.moveElements.success) {
        await refetch();
      }
    } catch (error) {
      // Handle error silently
    }
    
    setDraggedItem(null);
    setDropTarget(null);
  };

  if (loading) return <LoadingOverlay />;
  if (error) return <Box sx={{ flex: 1, p: 3 }}><Typography color="error">Error: {error.message}</Typography></Box>;
  
  if (items.length === 0) {
    return (
      <Paper sx={{ flex: 1, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Project Tree</Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<Add />}
            onClick={() => handleAddElement()}
          >
            Add element
          </Button>
        </Box>
        <Typography>No data available</Typography>
        
        <ProjectModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingElement(null);
            setParentElement(null);
          }}
          project={editingElement}
          parentId={parentElement ? parseInt(parentElement.id) : parentId}
          companies={companiesData?.companies?.list || []}
          onSave={handleSaveElement}
          onUploadComplete={handleUploadComplete}
          loadingText="Saving element..."
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ flex: 1, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, minHeight: '48px' }}>
        <Typography variant="h6">
          Project Tree
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="text"
            size="small"
            disabled={expandedItems.size === 0}
            onClick={() => setExpandedItems(new Set())}
          >
            Collapse All
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Add />}
            onClick={() => handleAddElement()}
          >
            Add new element
          </Button>
        </Box>
      </Box>
      <Box sx={{ 
        width: '100%', 
        height: '400px', 
        overflow: 'auto',
        border: '1px solid #b0d4f1',
        borderRadius: 2,
        p: 1
      }}>
        {/* Drop zone dla najwyższego poziomu */}
        <Box
          sx={{
            height: '30px',
            backgroundColor: dropTarget === 'root' ? 'action.hover' : 'transparent',
            border: dropTarget === 'root' ? '2px dashed #1976d2' : '2px dashed transparent',
            borderRadius: 1,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            color: 'text.secondary'
          }}
          onDragOver={(e) => {
            if (draggedItem) {
              e.preventDefault();
              setDropTarget('root');
            }
          }}
          onDragLeave={() => setDropTarget(null)}
          onDrop={(e) => {
            e.preventDefault();
            handleDrop('root');
          }}
        >
          {draggedItem ? 'Drop here to move to top level' : ''}
        </Box>
        {items.map(item => (
          <CustomTreeItem 
            key={item.id} 
            item={item} 
            draggedItem={draggedItem}
            setDraggedItem={setDraggedItem}
            dropTarget={dropTarget}
            setDropTarget={setDropTarget}
            onDrop={handleDrop}
            expandedItems={expandedItems}
            setExpandedItems={setExpandedItems}
            showDetails={showDetails}
            handleAddElement={handleAddElement}
            handleEditElement={handleEditElement}
            handleDeleteElement={handleDeleteElement}
            selectedUnit={selectedUnit}
            onUnitSelect={onUnitSelect}
          />
        ))}
      </Box>
      
      <ProjectModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingElement(null);
          setParentElement(null);
        }}
        project={editingElement}
        parentId={parentElement ? parseInt(parentElement.id) : parentId}
        companies={companiesData?.companies?.list || []}
        onSave={handleSaveElement}
        onUploadComplete={handleUploadComplete}
        loadingText="Saving element..."
      />
      
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Element</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{elementToDelete?.label}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
});

export default ProjectTree;